import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/auth.store';
import AppLayout from './components/layout/AppLayout';
import { ErrorBoundary } from './components/ErrorBoundary';

// Public pages — eager (needed for first paint)
import Landing from './pages/Landing';
import Login from './pages/Login';
import Legal from './pages/Legal';
import NotFound from './pages/NotFound';

// Authenticated pages — lazy loaded (code-split per route)
const Onboarding = lazy(() => import('./pages/Onboarding'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Agents = lazy(() => import('./pages/Agents'));
const AgentDetail = lazy(() => import('./pages/AgentDetail'));
const Leads = lazy(() => import('./pages/Leads'));
const LeadDetail = lazy(() => import('./pages/LeadDetail'));
const CommandCenter = lazy(() => import('./pages/CommandCenter'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Training = lazy(() => import('./pages/Training'));
const Settings = lazy(() => import('./pages/Settings'));
const Billing = lazy(() => import('./pages/Billing'));
const Marketplace = lazy(() => import('./pages/Marketplace'));
const AgentBuilder = lazy(() => import('./pages/AgentBuilder'));
const AgentLogs = lazy(() => import('./pages/AgentLogs'));
const Welcome = lazy(() => import('./pages/Welcome'));
const AgentBrain = lazy(() => import('./pages/AgentBrain'));
const Social = lazy(() => import('./pages/Social'));
const Integrations = lazy(() => import('./pages/Integrations'));

const SPINNER = (
  <div className="flex h-screen items-center justify-center bg-bot-base">
    <div className="flex flex-col items-center gap-3">
      <div className="w-8 h-8 rounded-full border-2 border-brand-500/20 border-t-brand-500 animate-spin" />
      <span className="text-xs text-ink-mid">Loading...</span>
    </div>
  </div>
);

/** Requires: logged in + onboarding complete */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuthStore();
  if (isLoading) return SPINNER;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.onboardingCompleted) return <Navigate to="/onboarding" replace />;
  return <>{children}</>;
}

/** Requires: logged in (but not necessarily onboarding complete) */
function RequireOnboarding({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuthStore();
  if (isLoading) return SPINNER;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/**
 * Requires an active paid subscription.
 * FREE tier users are redirected to /billing?upgrade=1 to choose a plan.
 * Place INSIDE RequireAuth so user/onboarding checks run first.
 */
// All features available to every tier — credit balance is the only limit
function RequireSubscription({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          {/* ── Public ───────────────────────────────────────────── */}
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/terms" element={<Legal />} />
          <Route path="/privacy" element={<Legal />} />

          {/* ── Onboarding — requires auth, NOT complete onboarding ─ */}
          <Route path="/onboarding" element={<RequireOnboarding><Suspense fallback={SPINNER}><Onboarding /></Suspense></RequireOnboarding>} />

          {/* ── Welcome / First Win — after onboarding, before billing ─ */}
          <Route path="/welcome" element={<RequireAuth><Suspense fallback={SPINNER}><Welcome /></Suspense></RequireAuth>} />

          {/* ── App shell ────────────────────────────────────────── */}
          <Route element={<RequireAuth><Suspense fallback={SPINNER}><AppLayout /></Suspense></RequireAuth>}>

            {/* Always accessible (FREE + paid) — overview + account */}
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/agents" element={<Agents />} />
            <Route path="/agents/:agentType" element={<AgentDetail />} />
            <Route path="/billing" element={<Billing />} />
            <Route path="/settings" element={<Settings />} />

            {/* Requires paid subscription ───────────────────────── */}
            <Route path="/agents/:agentType/logs" element={
              <RequireSubscription><AgentLogs /></RequireSubscription>
            } />
            <Route path="/leads" element={
              <RequireSubscription><Leads /></RequireSubscription>
            } />
            <Route path="/leads/:leadId" element={
              <RequireSubscription><LeadDetail /></RequireSubscription>
            } />
            <Route path="/command" element={
              <RequireSubscription><CommandCenter /></RequireSubscription>
            } />
            <Route path="/analytics" element={
              <RequireSubscription><Analytics /></RequireSubscription>
            } />
            <Route path="/training" element={
              <RequireSubscription><Training /></RequireSubscription>
            } />
            <Route path="/social" element={
              <RequireSubscription><Social /></RequireSubscription>
            } />
            <Route path="/marketplace" element={
              <RequireSubscription><Marketplace /></RequireSubscription>
            } />
            <Route path="/marketplace/build" element={
              <RequireSubscription><AgentBuilder /></RequireSubscription>
            } />
            <Route path="/brain" element={
              <RequireSubscription><AgentBrain /></RequireSubscription>
            } />
            <Route path="/integrations" element={
              <RequireSubscription><Integrations /></RequireSubscription>
            } />
          </Route>

          {/* ── 404 ──────────────────────────────────────────────── */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
